// User Authorization Check API
import { AuthorizationResponse, AuthorizationError, AuthorizationCheckResult } from '../typings/auth';
import { getAuthUserEndpoint } from '../config/env';
import logger from '../utils/logger';
/**
 * @brief Class to handle user authorization checks
 */
class UserAuthorizationCheck {
  private m_authUserEndpoint: string;

  /**
   * @brief Constructor to initialize the authorization check class
   */
  constructor() {
    this.m_authUserEndpoint = getAuthUserEndpoint();
    logger.debug('Auth User Endpoint:', this.m_authUserEndpoint);
  }

  /**
   * @brief Check user authorization using JWT token
   * @param token JWT token for authorization
   * @return User information object if authorized, error object otherwise
   */
  public async CheckAuthorization(token: string): Promise<AuthorizationCheckResult> {
    try {
      // Validate input token
      if (!token || typeof token !== 'string') {
        logger.error('Invalid token provided to CheckAuthorization');
        return {
          code: 'INVALID_TOKEN',
          message: 'Invalid token provided to CheckAuthorization'
        };
      }
  
      // Validate endpoint configuration
      if (!this.m_authUserEndpoint) {
        logger.error('REACT_APP_AUTH_USER_ENDPOINT not configured');
        return {
          code: 'ENDPOINT_NOT_CONFIGURED',
          message: 'REACT_APP_AUTH_USER_ENDPOINT not configured'
        };
      }
  
      // Prepare request headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Make GET request to authorization endpoint
      const response = await fetch(this.m_authUserEndpoint, {
        method: 'GET',
        headers: headers,
        credentials: 'include'  // Required for CORS with allowCredentials: true
      });

      const result = await response.json();
      if (!response.ok) {
        logger.error(`Authorization request failed with status: ${response.status}`);
        return {
          code: 'REQUEST_FAILED',
          message: `Authorization request failed with status: ${response.status}`
        };
      }

      // Parse response JSON
      
    const data: AuthorizationResponse = result;
    
      
      // Check response status
      if (data.status === 'success') {
        return data.data.userInfo;
      } else {
        logger.error('Authorization failed:', data.error?.message || 'Unknown error');
        return {
          code: data.error?.code || 'AUTHORIZATION_FAILED',
          message: data.error?.message || 'Authorization failed'
        };
      }

    } catch (error) {
      logger.error('Error during authorization check:', error);
      return {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error during authorization check'
      };
    }
  }

  /**
   * @brief Helper method to check if authorization result is an error
   * @param result Result from CheckAuthorization method
   * @return True if result is an error, false if it's user info
   */
  public static IsError(result: AuthorizationCheckResult): result is AuthorizationError {
    return 'code' in result && 'message' in result;
  }
}

export default UserAuthorizationCheck;